import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { apiFetch } from "../utils/api";
import { Badge, BadgeFormaPago, estadoVisible, mapaEstadosSolicitud } from "../utils/estados";
import { EncabezadoDetalle, tarjetaTema, thTema, tituloSeccionTema } from "../components/TemaDetalle";
import Cargando from "../components/Cargando";

const dinero = (n, simbolo = "C$") =>
  `${simbolo}${Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fecha = (v) => (v ? new Date(v).toLocaleDateString("es-NI") : "—");

// Detalle de una solicitud que el aprobador ya aprobó: todo lo que aprobó
// (con precio, subtotal, IVA y total) y lo que no quedó aprobado.
function DetalleMiAprobacion() {
  const { id } = useParams();
  const usuario = JSON.parse(localStorage.getItem("usuario") || "null");

  const [solicitud, setSolicitud] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    apiFetch(`/api/solicitudes/${id}`)
      .then(setSolicitud)
      .catch((err) => setError(err.message));
  }, [id]);

  if (error) return <p style={{ color: "#a3402f" }}>{error}</p>;
  if (!solicitud) return <Cargando />;

  const cotizaciones = solicitud.cotizaciones || [];
  const items = solicitud.items || [];
  // Las aprobaciones viejas, sin aprobado_por guardado, son del aprobador de la solicitud.
  const esMia = (it) => String(it.aprobado_por ?? solicitud.aprobador_id) === String(usuario?.id);

  const cotizacionDe = (it) => cotizaciones.find((c) => c.seleccionada && c.items.some((x) => x.id === it.id));

  // Lo que aprobé: una fila por producto, con el precio de la cotización elegida.
  const aprobados = items
    .filter((it) => Number(it.cantidad_aprobada) > 0 && esMia(it))
    .map((it) => {
      const cot = cotizacionDe(it);
      const linea = cot?.items.find((x) => x.id === it.id);
      const precio = linea?.precio != null ? Number(linea.precio) : null;
      const moneda = cot?.moneda || "NIO";
      // Todo queda en la moneda de la cotización (sin convertir).
      const subtotalNio = precio != null ? precio : 0;
      const ivaPct = Number(cot?.iva_porcentaje ?? 15);
      const iva = cot?.exento_iva ? 0 : subtotalNio * (ivaPct / 100);
      return {
        it,
        cot,
        precio,
        simbolo: moneda === "USD" ? "US$" : "C$",
        moneda,
        subtotalNio,
        iva,
        ivaPct,
        unitario: precio != null && it.cantidad_aprobada > 0 ? precio / it.cantidad_aprobada : null,
      };
    });

  // Una compra por proveedor: los productos aprobados se agrupan por su cotización.
  const gruposPorProveedor = [];
  aprobados.forEach((a) => {
    const clave = a.cot?.id ?? "sin-proveedor";
    let g = gruposPorProveedor.find((x) => x.clave === clave);
    if (!g) {
      g = {
        clave,
        proveedor: a.cot?.proveedor_nombre || "Sin proveedor",
        formaPago: a.cot?.forma_pago,
        simbolo: a.simbolo,
        ivaPct: a.ivaPct,
        lineas: [],
      };
      gruposPorProveedor.push(g);
    }
    g.lineas.push(a);
  });

  // Lo que NO quedó aprobado: cantidad pedida menos lo aprobado.
  const noAprobados = items
    .map((it) => {
      const sinAprobar = Math.max(0, Number(it.cantidad) - Number(it.cantidad_aprobada || 0));
      const rechazada = Math.min(Number(it.cantidad_descartada || 0), sinAprobar);
      const pendiente = sinAprobar - rechazada;
      return { it, sinAprobar, rechazada, pendiente };
    })
    .filter((x) => x.sinAprobar > 0);

  const proveedores = [...new Set(aprobados.map((a) => a.cot?.proveedor_nombre).filter(Boolean))];
  const fechasAprob = aprobados.map((a) => a.it.aprobado_en).filter(Boolean);
  const aprobadaEl = fechasAprob.length ? new Date(Math.max(...fechasAprob.map((f) => new Date(f).getTime()))) : null;
  const estado = estadoVisible(
    solicitud.estado,
    solicitud.total_cotizaciones,
    solicitud.tiene_pendiente_aprobacion,
    solicitud.en_atencion,
    solicitud.tiene_pago_pendiente,
    solicitud.tiene_pago_realizado
  );

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto" }}>
      <Link to="/aprobadas" style={styles.volver}>
        ← Regresar a mis aprobaciones
      </Link>
      <EncabezadoDetalle
        titulo={`✅ Aprobación #${String(solicitud.id).padStart(3, "0")}`}
        insignia={<Badge estado={estado} mapa={mapaEstadosSolicitud()} />}
        subtitulo={`${solicitud.solicitante_nombre} · ${solicitud.area_nombre}`}
      />

      <div style={styles.card}>
        <div style={styles.datos}>
          <Dato etiqueta="Solicitante" valor={solicitud.solicitante_nombre} />
          <Dato etiqueta="Área" valor={solicitud.area_nombre} />
          <Dato etiqueta="Edificio" valor={solicitud.edificio_nombre} />
          <Dato etiqueta="Fecha de solicitud" valor={fecha(solicitud.creado_en)} />
          <Dato etiqueta="Aprobada el" valor={fecha(aprobadaEl)} />
          <Dato etiqueta="Estado actual" valor={<Badge estado={estado} mapa={mapaEstadosSolicitud()} />} />
          <Dato etiqueta="Proveedor" valor={proveedores.join(", ") || "—"} />
        </div>
        <div style={styles.requerimiento}>
          <div style={styles.etiqueta}>Requerimiento</div>
          {solicitud.descripcion_requerimiento}
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={styles.tituloSeccion}>
          ✓ Productos aprobados <span style={styles.contador}>{aprobados.length}</span>
        </h3>
        {aprobados.length === 0 ? (
          <p style={styles.vacio}>No hay productos aprobados por ti en esta solicitud.</p>
        ) : (
          <>
            {/* Cada proveedor es una compra distinta: su propia tabla y sus propios totales. */}
            {gruposPorProveedor.map((g, gi) => {
              const subtotal = g.lineas.reduce((suma, a) => suma + a.subtotalNio, 0);
              const iva = g.lineas.reduce((suma, a) => suma + a.iva, 0);
              return (
                <div key={g.clave} style={{ marginTop: gi === 0 ? 0 : "1.4rem" }}>
                  <div style={styles.encabezadoProveedor}>
                    <strong style={{ color: "var(--tema-titulo, #0b3c60)" }}>
                      🏢 {g.proveedor}{" "}
                      <span style={{ fontWeight: 400, color: "#5b6472" }}>
                        · {g.lineas.length} producto{g.lineas.length === 1 ? "" : "s"}
                      </span>
                    </strong>
                    <BadgeFormaPago valor={g.formaPago} />
                  </div>
                  <div className="table-scroll" style={styles.tablaWrap}>
                    <table style={styles.tabla}>
                      <thead>
                        <tr>
                          <th style={{ ...styles.th, width: 40, textAlign: "center" }}>#</th>
                          <th style={styles.th}>Producto</th>
                          <th style={{ ...styles.th, textAlign: "center" }}>Cantidad aprobada</th>
                          <th style={{ ...styles.th, textAlign: "right" }}>P. unitario</th>
                          <th style={{ ...styles.th, textAlign: "right" }}>Subtotal</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.lineas.map((a, i) => (
                          <tr key={a.it.id} style={{ borderTop: "1px solid #eef1f3", background: i % 2 === 1 ? "#fafbfc" : "#fff" }}>
                            <td style={{ ...styles.td, textAlign: "center", color: "#8993a1" }}>{i + 1}</td>
                            <td style={{ ...styles.td, fontWeight: 600 }}>{a.it.descripcion_producto}</td>
                            <td style={{ ...styles.td, textAlign: "center", whiteSpace: "nowrap" }}>
                              <span style={styles.pastillaVerde}>
                                {a.it.cantidad_aprobada}
                                {a.it.unidad ? ` ${a.it.unidad}` : ""}
                              </span>
                              {Number(a.it.cantidad_aprobada) < Number(a.it.cantidad) && (
                                <div style={styles.sub}>de {a.it.cantidad} pedidas</div>
                              )}
                            </td>
                            <td style={{ ...styles.td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                              {a.unitario != null ? dinero(a.unitario, a.simbolo) : "—"}
                            </td>
                            <td style={{ ...styles.td, textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                              {a.precio != null ? dinero(a.precio, a.simbolo) : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={styles.desglose}>
                    <div style={styles.linea}>
                      <span>Subtotal</span>
                      <span>{dinero(subtotal, g.simbolo)}</span>
                    </div>
                    <div style={styles.linea}>
                      <span>IVA {g.ivaPct}%</span>
                      <span>{dinero(iva, g.simbolo)}</span>
                    </div>
                    <div style={{ ...styles.linea, ...styles.lineaTotal }}>
                      <span>Total aprobado — {g.proveedor}</span>
                      <span>{dinero(subtotal + iva, g.simbolo)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={styles.tituloSeccion}>
          ✕ Productos no aprobados <span style={styles.contador}>{noAprobados.length}</span>
        </h3>
        {noAprobados.length === 0 ? (
          <p style={styles.vacio}>Todo lo solicitado quedó aprobado.</p>
        ) : (
          <div className="table-scroll" style={styles.tablaWrap}>
            <table style={styles.tabla}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, width: 40, textAlign: "center" }}>#</th>
                  <th style={styles.th}>Producto</th>
                  <th style={{ ...styles.th, textAlign: "center" }}>Pedidas</th>
                  <th style={{ ...styles.th, textAlign: "center" }}>Sin aprobar</th>
                  <th style={styles.th}>Situación</th>
                </tr>
              </thead>
              <tbody>
                {noAprobados.map((x, i) => (
                  <tr key={x.it.id} style={{ borderTop: "1px solid #eef1f3", background: i % 2 === 1 ? "#fafbfc" : "#fff" }}>
                    <td style={{ ...styles.td, textAlign: "center", color: "#8993a1" }}>{i + 1}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{x.it.descripcion_producto}</td>
                    <td style={{ ...styles.td, textAlign: "center" }}>
                      {x.it.cantidad}
                      {x.it.unidad ? ` ${x.it.unidad}` : ""}
                    </td>
                    <td style={{ ...styles.td, textAlign: "center", fontWeight: 700 }}>{x.sinAprobar}</td>
                    <td style={styles.td}>
                      {x.rechazada > 0 && (
                        <span style={{ ...styles.estado, background: "#f7ded9", color: "#a3402f" }}>
                          No aprobado por gerencia ({x.rechazada})
                        </span>
                      )}{" "}
                      {x.pendiente > 0 && (
                        <span style={{ ...styles.estado, background: "#fde6cf", color: "#b85a0b" }}>
                          Pendiente ({x.pendiente})
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Dato({ etiqueta, valor }) {
  return (
    <div>
      <div style={styles.etiqueta}>{etiqueta}</div>
      <div style={styles.valor}>{valor}</div>
    </div>
  );
}

const styles = {
  volver: { color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, fontSize: "0.88rem", textDecoration: "none" },
  card: { background: "#fff", border: "1px solid #e2e6ea", borderRadius: 14, padding: "1.1rem 1.3rem", marginTop: "1rem", boxShadow: "0 1px 3px rgba(27,42,74,0.06)", ...tarjetaTema },
  datos: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: "0.9rem 1.4rem" },
  etiqueta: { fontSize: "0.68rem", fontWeight: 700, color: "#8993a1", textTransform: "uppercase", letterSpacing: "0.02em", marginBottom: "0.15rem" },
  valor: { fontSize: "0.9rem", color: "var(--tema-titulo, #0b3c60)", fontWeight: 600, overflowWrap: "anywhere" },
  requerimiento: { marginTop: "1rem", padding: "0.6rem 0.8rem", background: "#fafbfc", border: "1px solid #eef1f3", borderRadius: 8, fontSize: "0.9rem", color: "var(--tema-titulo, #0b3c60)" },
  tituloSeccion: { ...tituloSeccionTema, margin: "0 0 0.8rem" },
  contador: { marginLeft: "0.3rem", fontSize: "0.74rem", fontWeight: 700, color: "#5b6472", background: "#eef1f3", borderRadius: 999, padding: "0.1rem 0.55rem" },
  vacio: { margin: 0, textAlign: "center", color: "#8993a1", fontSize: "0.88rem" },
  tablaWrap: { border: "1px solid #e2e6ea", borderRadius: 10, overflow: "auto" },
  tabla: { width: "100%", borderCollapse: "collapse", fontSize: "0.86rem", color: "var(--tema-titulo, #0b3c60)" },
  th: { padding: "0.55rem 0.7rem", ...thTema, fontSize: "0.72rem", fontWeight: 700, textAlign: "left", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.02em" },
  td: { padding: "0.55rem 0.7rem", verticalAlign: "middle" },
  sub: { fontSize: "0.72rem", color: "#8993a1", marginTop: "0.1rem" },
  pastillaVerde: { display: "inline-block", background: "#dcf0e6", color: "#2f7a54", borderRadius: 999, padding: "0.1rem 0.65rem", fontSize: "0.8rem", fontWeight: 700 },
  estado: { display: "inline-block", borderRadius: 999, padding: "0.15rem 0.65rem", fontSize: "0.74rem", fontWeight: 700, whiteSpace: "nowrap" },
  encabezadoProveedor: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "0.6rem",
    flexWrap: "wrap",
    background: "var(--tema-suave, #eef3fa)",
    border: "1px solid var(--tema-linea, #d5deec)",
    borderBottom: "none",
    borderRadius: "10px 10px 0 0",
    padding: "0.5rem 0.8rem",
    fontSize: "0.9rem",
  },
  desglose: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.2rem", marginTop: "0.9rem", fontSize: "0.88rem", color: "#5b6472", fontVariantNumeric: "tabular-nums" },
  linea: { display: "flex", justifyContent: "space-between", gap: "2rem", minWidth: 260 },
  lineaTotal: { marginTop: "0.25rem", paddingTop: "0.4rem", borderTop: "1px solid #d3d9df", fontSize: "1.05rem", fontWeight: 700, color: "var(--tema-titulo, #0b3c60)" },
  nota: { fontSize: "0.74rem", color: "#8993a1", marginTop: "0.15rem" },
};

export default DetalleMiAprobacion;
